import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Input, message, App, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import MainLayout from '../../components/Layout/MainLayout';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';

interface Admin {
  Admin_IDX: number;
  Admin_ID: string;
  Admin_Name: string;
  Admin_Pass?: string;
  Admin_Grade: number;
  Confirm_Grade: number;
  Admin_Position: string;
  Admin_Email: string;
  Admin_Cell: string;
}

const AdminList: React.FC = () => {
  const { modal: antModal } = App.useApp();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [adminList, setAdminList] = useState<Admin[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<Admin>>({});

  // 관리자 목록 조회
  const fetchAdminList = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/list');
      if (response.data.resultCode === 200) {
        setAdminList(response.data.data);
      }
    } catch (error) {
      console.error('관리자 목록 조회 오류:', error);
      message.error('관리자 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminList();
  }, []);

  // 등록/수정 모달 열기
  const openModal = (admin?: Admin) => {
    if (admin) {
      setEditMode(true);
      setFormData(admin);
    } else {
      setEditMode(false);
      setFormData({ Admin_Grade: 1, Confirm_Grade: 0 }); // 기본값: 관리자, 승인권한 없음
    }
    setModalVisible(true);
  };

  // 저장
  const handleSave = async () => {
    // 등록 시에는 비밀번호 필수, 수정 시에는 선택
    if (!formData.Admin_ID || !formData.Admin_Name) {
      message.warning('아이디와 이름은 필수입니다.');
      return;
    }

    if (!editMode && !formData.Admin_Pass) {
      message.warning('비밀번호를 입력해주세요.');
      return;
    }

    try {
      const url = editMode ? '/api/admin/update' : '/api/admin/insert';
      const response = await api.post(url, formData);

      if (response.data.resultCode === 200) {
        message.success(editMode ? '수정되었습니다.' : '등록되었습니다.');
        setModalVisible(false);
        fetchAdminList();
      } else {
        message.error(response.data.message || '저장 실패');
      }
    } catch (error) {
      console.error('저장 오류:', error);
      message.error('저장 중 오류가 발생했습니다.');
    }
  };

  // 삭제
  const handleDelete = (admin: Admin) => {
    antModal.confirm({
      title: '관리자 삭제',
      content: `${admin.Admin_Name}(${admin.Admin_ID})를 삭제하시겠습니까?`,
      okText: '삭제',
      okType: 'danger',
      cancelText: '취소',
      onOk: async () => {
        try {
          const response = await api.post('/api/admin/delete', {
            Admin_IDX: admin.Admin_IDX
          });

          if (response.data.resultCode === 200) {
            message.success('삭제되었습니다.');
            fetchAdminList();
          } else {
            message.error(response.data.message || '삭제 실패');
          }
        } catch (error) {
          console.error('삭제 오류:', error);
          message.error('삭제 중 오류가 발생했습니다.');
        }
      },
    });
  };

  const columns = [
    {
      title: 'No',
      key: 'no',
      width: 80,
      align: 'center' as const,
      render: (_: any, __: any, index: number) => index + 1
    },
    { title: '아이디', dataIndex: 'Admin_ID', width: 120 },
    { title: '이름', dataIndex: 'Admin_Name', width: 100 },
    { title: '직급', dataIndex: 'Admin_Position', width: 100 },
    {
      title: '등급',
      dataIndex: 'Admin_Grade',
      width: 100,
      align: 'center' as const,
      render: (value: number) => {
        const gradeMap: { [key: number]: string } = {
          0: '일반',
          1: '관리자',
          2: '최고관리자'
        };
        return gradeMap[value] || '일반';
      }
    },
    {
      title: '승인권한',
      dataIndex: 'Confirm_Grade',
      width: 100,
      align: 'center' as const,
      render: (value: number) => value === 2 ? '있음' : '없음'
    },
    { title: '이메일', dataIndex: 'Admin_Email', width: 200 },
    { title: '연락처', dataIndex: 'Admin_Cell', width: 130 },
    {
      title: '관리',
      key: 'action',
      width: 150,
      align: 'center' as const,
      render: (_: any, record: Admin) => (
        <>
          <Button size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            수정
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            style={{ marginLeft: 8 }}
          >
            삭제
          </Button>
        </>
      ),
    },
  ];

  return (
    <MainLayout>
      <Card
        title="관리자 관리"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
            관리자 등록
          </Button>
        }
      >
        <Table
          dataSource={adminList}
          columns={columns}
          rowKey="Admin_IDX"
          loading={loading}
          size="small"
        />
      </Card>

      {/* 등록/수정 모달 */}
      <Modal
        title={editMode ? '관리자 수정' : '관리자 등록'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        okText="저장"
        cancelText="취소"
        width={600}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: '8px', fontWeight: 'bold', width: '100px', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                아이디
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input
                  value={formData.Admin_ID}
                  onChange={(e) => setFormData({ ...formData, Admin_ID: e.target.value })}
                  disabled={editMode}
                  placeholder="아이디 입력"
                />
              </td>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                비밀번호
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input.Password
                  value={formData.Admin_Pass}
                  onChange={(e) => setFormData({ ...formData, Admin_Pass: e.target.value })}
                  placeholder={editMode ? '변경 시에만 입력' : '비밀번호 입력'}
                />
              </td>
            </tr>
            <tr>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                이름
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input
                  value={formData.Admin_Name}
                  onChange={(e) => setFormData({ ...formData, Admin_Name: e.target.value })}
                  placeholder="이름 입력"
                />
              </td>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                직급
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input
                  value={formData.Admin_Position}
                  onChange={(e) => setFormData({ ...formData, Admin_Position: e.target.value })}
                  placeholder="직급 입력"
                />
              </td>
            </tr>
            <tr>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                등급
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input value="관리자" disabled />
              </td>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                승인권한
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Select
                  style={{ width: '100%' }}
                  value={formData.Confirm_Grade}
                  onChange={(value) => setFormData({ ...formData, Confirm_Grade: value })}
                  disabled={user?.Confirm_Grade !== 2}
                  options={[
                    { label: '없음', value: 0 },
                    { label: '있음', value: 2 },
                  ]}
                />
                {user?.Confirm_Grade !== 2 && (
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>
                    승인권한이 있는 관리자만 지정 가능합니다.
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                이메일
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input
                  value={formData.Admin_Email}
                  onChange={(e) => setFormData({ ...formData, Admin_Email: e.target.value })}
                  placeholder="이메일 입력"
                />
              </td>
              <td style={{ padding: '8px', fontWeight: 'bold', backgroundColor: '#fafafa', border: '1px solid #d9d9d9' }}>
                연락처
              </td>
              <td style={{ padding: '8px', border: '1px solid #d9d9d9' }}>
                <Input
                  value={formData.Admin_Cell}
                  onChange={(e) => setFormData({ ...formData, Admin_Cell: e.target.value })}
                  placeholder="연락처 입력"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </Modal>
    </MainLayout>
  );
};

export default AdminList;
